/**
 * Native Supabase Auth OAuth (Google sign-in / sign-up).
 *
 * Web uses supabase.auth.signInWithOAuth() which navigates the current page to
 * Google. Inside Capacitor that leaves the WebView (or opens Safari) and breaks
 * the return path.
 *
 * On native we:
 *  1. Ask Supabase for the authorize URL with skipBrowserRedirect
 *  2. Open it in @capacitor/browser (SFSafariViewController / Custom Tabs)
 *  3. Redirect to an HTTPS bridge URL (reliable with Supabase PKCE). That page
 *     (or Universal Links) hands off to our custom URL scheme with the params.
 *  4. Establish the session via exchangeCodeForSession (PKCE) or setSession
 *
 * The desktop app (Electron) does the same with the system browser: Google
 * refuses sign-in inside embedded browsers. The bridge page gets
 * `?client=desktop` and forwards to `lighthouse://auth/callback`, which the
 * shell hands back to the window that started the flow.
 *
 * Inbox Gmail/Outlook OAuth is separate — see nativeOAuth.ts.
 *
 * Supabase Dashboard → Authentication → URL Configuration must allow:
 *   https://www.joinlighthouse.ai/auth/callback
 *   https://www.joinlighthouse.ai/auth/callback?client=desktop (or a `**` pattern)
 *   com.joinlighthouse.app://auth/callback
 */
import { ref } from 'vue'
import { Browser } from '@capacitor/browser'
import { supabase } from '@/supabase'
import { isNativePlatform } from './platform'
import { isProductionEnv } from './env'
import { isDesktopApp } from '@/desktop/bridge'
import { isDesktopAuthCallback, waitForDesktopDeepLink } from '@/desktop/deepLinks'
import { onAppUrlOpen } from '@/native/appUrl'

/**
 * Custom scheme that opens the installed app. Used by AuthNativeCallback.vue
 * when the HTTPS bridge loads inside the in-app browser.
 */
export const NATIVE_AUTH_APP_SCHEME = 'com.joinlighthouse.app://auth/callback'

/**
 * HTTPS redirect registered with Supabase. Prefer this over a bare custom
 * scheme — GoTrue's redirect URL rewriting is unreliable for `scheme://host/path`
 * and often returns the app with no `code` query param.
 */
export const NATIVE_AUTH_HTTPS_REDIRECT_URI = 'https://www.joinlighthouse.ai/auth/callback'

/** @deprecated Use NATIVE_AUTH_APP_SCHEME — kept so existing imports keep working. */
export const NATIVE_AUTH_REDIRECT_URI = NATIVE_AUTH_APP_SCHEME

/**
 * True while a native Google attempt is in flight, including the post-callback
 * check that rejects brand-new accounts (the native app is login-only). Auth
 * listeners must not route into the app while this is set.
 */
export const nativeGoogleAuthPending = ref(false)

const NATIVE_AUTH_TIMEOUT_MS = 5 * 60 * 1000
const BROWSER_FINISHED_GRACE_MS = 2000

function isAuthCallbackUrl(url: string): boolean {
    return (
        url.startsWith(NATIVE_AUTH_HTTPS_REDIRECT_URI)
        || url.startsWith(NATIVE_AUTH_APP_SCHEME)
        || url.startsWith('com.joinlighthouse.app://auth')
    )
}

/**
 * Parse OAuth params from a deep link. Supabase may put `code` in the query
 * (PKCE) or tokens / `code` in the hash (implicit). Custom-scheme URLs are also
 * flaky with `new URL()`, so we split the raw string.
 */
function parseAuthCallbackParams(url: string): {
    code: string | null
    error: string | null
    errorDescription: string | null
    accessToken: string | null
    refreshToken: string | null
} {
    const hashIdx = url.indexOf('#')
    const queryIdx = url.indexOf('?')

    const query = queryIdx >= 0
        ? url.slice(queryIdx + 1, hashIdx >= 0 ? hashIdx : undefined)
        : ''
    const hash = hashIdx >= 0 ? url.slice(hashIdx + 1) : ''

    const queryParams = new URLSearchParams(query)
    const hashParams = new URLSearchParams(hash)
    const pick = (key: string) => queryParams.get(key) || hashParams.get(key)

    return {
        code: pick('code'),
        error: pick('error'),
        errorDescription: pick('error_description'),
        accessToken: pick('access_token'),
        refreshToken: pick('refresh_token'),
    }
}

/**
 * Finish a Supabase OAuth round trip from its callback URL (custom scheme or
 * HTTPS): exchange the PKCE code, or set the implicit-flow tokens.
 */
export async function establishSessionFromCallback(url: string): Promise<void> {
    const params = parseAuthCallbackParams(url)

    if (params.error) {
        let description = params.errorDescription || params.error
        try {
            description = decodeURIComponent(description.replace(/\+/g, ' '))
        }
        catch {
            // keep raw
        }
        throw new Error(description)
    }

    if (params.code) {
        const { error } = await supabase.auth.exchangeCodeForSession(params.code)
        if (error) throw error
        return
    }

    if (params.accessToken && params.refreshToken) {
        const { error } = await supabase.auth.setSession({
            access_token: params.accessToken,
            refresh_token: params.refreshToken,
        })
        if (error) throw error
        return
    }

    const hasQuery = url.includes('?')
    const hasHash = url.includes('#')
    throw new Error(
        `Sign-in did not return an authorization code.`
        + ` (callback had query=${hasQuery} hash=${hasHash})`,
    )
}

/** Query param that tells the HTTPS bridge page to hand off to the desktop app. */
export const DESKTOP_AUTH_CLIENT_PARAM = 'client'
export const DESKTOP_AUTH_CLIENT = 'desktop'
const PRODUCTION_ORIGIN = new URL(NATIVE_AUTH_HTTPS_REDIRECT_URI).origin

/**
 * The bridge page the desktop sign-in returns to: production's, or the
 * current deployment's (staging, local dev) so the round trip stays on the
 * Supabase project this build talks to.
 */
export function desktopAuthRedirectUri(): string {
    let origin = PRODUCTION_ORIGIN
    if (!isProductionEnv()) {
        try {
            origin = new URL(import.meta.env.VITE_APP_BASE_URL || PRODUCTION_ORIGIN).origin
        }
        catch {
            origin = PRODUCTION_ORIGIN
        }
    }
    const url = new URL('/auth/callback', origin)
    url.searchParams.set(DESKTOP_AUTH_CLIENT_PARAM, DESKTOP_AUTH_CLIENT)
    return url.toString()
}

export interface NativeAuthOAuthOptions {
    /** Extra query params on the final redirect. */
    redirectQuery?: Record<string, string>
    queryParams?: Record<string, string>
    data?: Record<string, unknown>
}

/**
 * Runs Google OAuth in the system in-app browser and establishes a Supabase
 * session. Resolves when the session is set. Rejects on cancel / timeout /
 * provider error.
 */
export async function signInWithGoogleNative(
    options: NativeAuthOAuthOptions = {},
): Promise<void> {
    if (!isNativePlatform()) {
        throw new Error('signInWithGoogleNative is only for Capacitor native apps')
    }

    let redirectTo = NATIVE_AUTH_HTTPS_REDIRECT_URI
    if (options.redirectQuery && Object.keys(options.redirectQuery).length > 0) {
        const redirectUrl = new URL(NATIVE_AUTH_HTTPS_REDIRECT_URI)
        for (const [key, value] of Object.entries(options.redirectQuery)) {
            redirectUrl.searchParams.set(key, value)
        }
        redirectTo = redirectUrl.toString()
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo,
            skipBrowserRedirect: true,
            queryParams: {
                prompt: 'select_account',
                ...options.queryParams,
            },
            ...(options.data ? { data: options.data } : {}),
        },
    })

    if (error) throw error
    if (!data?.url) throw new Error('No OAuth URL returned from Supabase')

    await new Promise<void>((resolve, reject) => {
        let stopUrlOpen: (() => void) | undefined
        let browserClosedHandle: { remove: () => void } | undefined
        let settled = false
        let browserFinishedTimer: ReturnType<typeof setTimeout> | undefined

        const cleanup = () => {
            stopUrlOpen?.()
            browserClosedHandle?.remove()
            if (browserFinishedTimer) clearTimeout(browserFinishedTimer)
        }

        const fail = (err: Error) => {
            if (settled) return
            settled = true
            clearTimeout(timeout)
            cleanup()
            Browser.close().catch(() => {})
            reject(err)
        }

        const timeout = setTimeout(() => {
            fail(new Error('The sign-in request timed out. Please try again.'))
        }, NATIVE_AUTH_TIMEOUT_MS)

        // Through the app's one appUrlOpen dispatcher (src/native/appUrl.ts), so a callback that arrives while the
        // listener is still attaching (or on a cold start) is not lost. Consumes only its own callback URL.
        stopUrlOpen = onAppUrlOpen((url) => {
            if (settled || !isAuthCallbackUrl(url)) return false

            void (async () => {
                try {
                    await establishSessionFromCallback(url)
                    if (settled) return
                    settled = true
                    clearTimeout(timeout)
                    cleanup()
                    await Browser.close().catch(() => {})
                    resolve()
                }
                catch (err) {
                    fail(err instanceof Error ? err : new Error('Sign-in failed.'))
                }
            })()
            return true
        })

        Browser.addListener('browserFinished', () => {
            if (settled) return
            browserFinishedTimer = setTimeout(() => {
                fail(new Error('cancelled'))
            }, BROWSER_FINISHED_GRACE_MS)
        }).then((handle) => {
            browserClosedHandle = handle
        })

        Browser.open({
            url: data.url!,
            presentationStyle: 'popover',
        }).catch((err) => {
            fail(err instanceof Error ? err : new Error('Unable to open sign-in browser.'))
        })
    })
}

/**
 * Desktop app: Google sign-in in the system browser. Resolves once the
 * Supabase session is set in this window. Rejects with `cancelled` when the
 * creator comes back without finishing (a late return still signs them in,
 * through the deep-link fallback in src/desktop/init.ts).
 */
export async function signInWithGoogleDesktop(
    options: Pick<NativeAuthOAuthOptions, 'queryParams' | 'data'> = {},
): Promise<void> {
    if (!isDesktopApp()) {
        throw new Error('signInWithGoogleDesktop is only for the Lighthouse desktop app')
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo: desktopAuthRedirectUri(),
            skipBrowserRedirect: true,
            queryParams: {
                prompt: 'select_account',
                ...options.queryParams,
            },
            ...(options.data ? { data: options.data } : {}),
        },
    })

    if (error) throw error
    if (!data?.url) throw new Error('No OAuth URL returned from Supabase')

    const link = await waitForDesktopDeepLink(data.url, isDesktopAuthCallback, {
        kind: 'auth',
        timeoutMs: NATIVE_AUTH_TIMEOUT_MS,
    })
    await establishSessionFromCallback(link)
}
